import { toLocalDateKey } from '@/utils/localDateUtils';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import api from '@/services/api';
import MainLayout from '@/components/layout/MainLayout';
import { useDialog } from '@/hooks/useDialog';

export default function AdvanceTenantsReport() {
  const { showAlert } = useDialog();
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState<'pdf' | 'excel' | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/reports/advanced-payments?type=inquilino');
        setRows(res.data?.data?.rows || res.data?.rows || []);
      } catch (error) {
        console.error('Error loading report:', error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const exportPdf = async () => {
    setExporting('pdf');
    try {
      const res = await api.get('/reports/advanced-payments?export=pdf&type=inquilino');
      const filePath = res.data?.data?.file || res.data?.file;

      if (filePath) {
        const baseURL = (api.defaults as any).baseURL || 'http://localhost/backend';
        const fileUrl = `${baseURL}/${filePath}`;
        window.open(fileUrl, '_blank');
      }
    } catch (error) {
      console.error('Error exporting PDF:', error);
      showAlert({
        type: 'error',
        title: 'Error',
        message: 'Error al exportar PDF. Por favor, intente nuevamente.',
      });
    } finally {
      setExporting(null);
    }
  };

  const exportExcel = async () => {
    setExporting('excel');
    try {
      const res = await api.get('/reports/advanced-payments?export=excel&type=inquilino');
      const filePath = res.data?.data?.file || res.data?.file;

      if (filePath) {
        const baseURL = (api.defaults as any).baseURL || 'http://localhost/backend';
        const fileUrl = `${baseURL}/${filePath}`;

        const response = await axios.get(fileUrl, {
          responseType: 'blob',
          withCredentials: true
        });

        const url = window.URL.createObjectURL(response.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `alquileres-adelantados-inquilinos-${toLocalDateKey(new Date())}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Error exporting Excel:', error);
      showAlert({
        type: 'error',
        title: 'Error',
        message: 'Error al exportar Excel. Por favor, intente nuevamente.',
      });
    } finally {
      setExporting(null);
    }
  };

  return (
    <MainLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
            <Link to="/reports" className="hover:text-gray-900">Reportes</Link>
            <span>/</span>
            <span className="text-gray-900">Alquileres Adelantados - Inquilinos</span>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-1 flex items-center gap-2">
                <span className="text-3xl">💚</span>
                Alquileres Adelantados - Inquilinos
              </h1>
              <p className="text-gray-600">Pagos de inquilinos realizados anticipadamente</p>
            </div>
            <div className="flex gap-2">
              <button
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                onClick={exportPdf}
                disabled={exporting !== null}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                {exporting === 'pdf' ? 'Exportando...' : 'Exportar PDF'}
              </button>
              <button
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                onClick={exportExcel}
                disabled={exporting !== null}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {exporting === 'excel' ? 'Exportando...' : 'Exportar Excel'}
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mb-2"></div>
                <p className="text-gray-600">Cargando reporte...</p>
              </div>
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📋</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No hay pagos adelantados</h3>
              <p className="text-gray-600">No se encontraron pagos de inquilinos adelantados</p>
            </div>
          ) : (
            <>
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">{rows.length}</span> registro{rows.length !== 1 ? 's' : ''} encontrado{rows.length !== 1 ? 's' : ''}
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {Object.keys(rows[0] || {}).map((k) => (
                        <th key={k} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {k}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {rows.map((r, i) => (
                      <tr key={i} className="hover:bg-gray-50 transition-colors">
                        {Object.keys(rows[0] || {}).map((k) => (
                          <td key={k} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {String(r[k] ?? '')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
